import React from 'react';
import { useTranslation } from '../../../../i18n/hooks/useTranslation';
import { GaugeIcon, FaceIcon } from './icons';

type Level = 1 | 2 | 3 | 4 | 5;

interface ScaleRefine {
  triggerFrom: number;
  labelKey: string;
  options: { value: string; labelKey: string }[];
}

interface ScaleQuestionProps {
  questionNumber: number;
  labelKey: string;
  variant: 'demand' | 'feel';
  options: { value: number; labelKey: string }[];
  value: number | null;
  onChange: (value: number | null) => void;
  /** Optional follow-up chips, revealed when `value >= refine.triggerFrom`. */
  refine?: ScaleRefine;
  factor?: string | null;
  onFactorChange?: (factor: string | null) => void;
}

const palette = {
  demand: {
    icon: ['text-demand-1', 'text-demand-2', 'text-demand-3', 'text-demand-4', 'text-demand-5'],
    bg: ['bg-demand-soft-1', 'bg-demand-soft-2', 'bg-demand-soft-3', 'bg-demand-soft-4', 'bg-demand-soft-5'],
    border: ['border-demand-1', 'border-demand-2', 'border-demand-3', 'border-demand-4', 'border-demand-5'],
    label: ['text-demand-strong-1', 'text-demand-strong-2', 'text-demand-strong-3', 'text-demand-strong-4', 'text-demand-strong-5'],
  },
  feel: {
    icon: ['text-feel-1', 'text-feel-2', 'text-feel-3', 'text-feel-4', 'text-feel-5'],
    bg: ['bg-feel-soft-1', 'bg-feel-soft-2', 'bg-feel-soft-3', 'bg-feel-soft-4', 'bg-feel-soft-5'],
    border: ['border-feel-1', 'border-feel-2', 'border-feel-3', 'border-feel-4', 'border-feel-5'],
    label: ['text-feel-strong-1', 'text-feel-strong-2', 'text-feel-strong-3', 'text-feel-strong-4', 'text-feel-strong-5'],
  },
} as const;

export function ScaleQuestion({
  questionNumber,
  labelKey,
  variant,
  options,
  value,
  onChange,
  refine,
  factor = null,
  onFactorChange,
}: ScaleQuestionProps) {
  const { t } = useTranslation();
  const colors = palette[variant];
  const Icon = variant === 'demand' ? GaugeIcon : FaceIcon;

  const showRefine = Boolean(refine && value !== null && value >= refine.triggerFrom);

  return (
    <div className="bg-white rounded-xl p-4 mb-3 shadow-sm">
      <div className="flex items-center gap-1.5 mb-3">
        <span className="inline-flex items-center justify-center w-[22px] h-[22px] rounded-full bg-amber-500 text-white text-xs font-medium shrink-0">
          {questionNumber}
        </span>
        <span className="text-[15px] font-semibold text-gray-800">{t(labelKey)}</span>
      </div>
      <div className="grid grid-cols-5 gap-1.5" role="radiogroup">
        {options.map((opt) => {
          const isSelected = value === opt.value;
          const idx = (opt.value - 1) as 0 | 1 | 2 | 3 | 4;
          const iconColor = colors.icon[idx];
          const bgColor = isSelected ? colors.bg[idx] : 'bg-stone-100';
          const borderColor = isSelected ? colors.border[idx] : 'border-gray-200';
          const labelColor = isSelected ? `${colors.label[idx]} font-semibold` : 'text-gray-500 font-medium';

          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              onClick={() => {
                const next = isSelected ? null : opt.value;
                onChange(next);
                // Dropping below the trigger (or clearing) discards the factor:
                // it only makes sense while the chips are on screen.
                if (refine && (next === null || next < refine.triggerFrom)) {
                  onFactorChange?.(null);
                }
              }}
              className={`flex flex-col items-center justify-center gap-1 min-h-[64px] min-w-0 p-2 rounded-[10px] border-[1.5px] transition-all active:scale-95 ${bgColor} ${borderColor}`}
            >
              <span className={`flex items-center justify-center ${iconColor}`}>
                <Icon
                  level={opt.value as Level}
                  className={variant === 'demand' ? 'w-9 h-6' : 'w-8 h-8'}
                />
              </span>
              <span className={`w-full text-[11px] leading-tight text-center break-words hyphens-auto ${labelColor}`}>
                {t(opt.labelKey)}
              </span>
            </button>
          );
        })}
      </div>

      {showRefine && refine && (
        <div className="mt-3.5 pt-3 border-t border-gray-200">
          <div className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">
            {t(refine.labelKey)}
          </div>
          <div className="flex flex-wrap gap-2" role="radiogroup">
            {refine.options.map((opt) => {
              const isSelected = factor === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => onFactorChange?.(isSelected ? null : opt.value)}
                  className={`rounded-full px-4 py-2 text-sm border-[1.5px] transition-all active:scale-95 ${
                    isSelected
                      ? 'bg-amber-100 text-amber-700 font-semibold border-amber-500'
                      : 'bg-stone-100 text-gray-500 font-medium border-gray-200 hover:bg-stone-200'
                  }`}
                >
                  {t(opt.labelKey)}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
